import { useState, useEffect } from 'react';
import { 
  ChevronLeft,
  ChevronRight,
  Plus,
  Trash2,
  Search,
  Edit,
  X,
  Key,
  Mail,
  Eye,
  EyeOff,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';

const API = `${window.location.origin}/api`;

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pages: 1 });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    role: 'user',
    melanin_balance: 0
  });

  const fetchUsers = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString() });
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await fetch(`${API}/admin/users?${params.toString()}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
        setPagination({ page: data.page, pages: data.pages });
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [searchQuery]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
  };

  const updateRole = async (userId, newRole) => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}/role?role=${newRole}`, {
        method: 'PUT',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success(`User role updated to ${newRole}`);
        fetchUsers(pagination.page);
      }
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error('Failed to update role');
    }
  };

  const toggleUserFeature = async (userId, feature, currentValue) => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}/feature`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ [feature]: !currentValue })
      });

      if (response.ok) {
        toast.success(`Feature ${!currentValue ? 'enabled' : 'disabled'}`);
        fetchUsers(pagination.page);
      } else {
        toast.error('Failed to update feature');
      }
    } catch (error) {
      console.error('Error toggling feature:', error);
      toast.error('Failed to update feature');
    }
  };

  const openCreateDialog = () => {
    setUserForm({
      name: '',
      email: '',
      role: 'user',
      melanin_balance: 0
    });
    setCreateDialogOpen(true);
  };

  const handleCreate = async () => {
    if (!userForm.name || !userForm.email) {
      toast.error('Name and email are required');
      return;
    }

    try {
      const response = await fetch(`${API}/admin/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(userForm)
      });

      if (response.ok) {
        toast.success('User created successfully');
        fetchUsers(pagination.page);
        setCreateDialogOpen(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create user');
      }
    } catch (error) {
      toast.error('Failed to create user');
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setUserForm({
      name: user.name || '',
      email: user.email || '',
      role: user.role || 'user',
      melanin_balance: user.melanin_balance || 0
    });
    setEditDialogOpen(true);
  };

  const handleEdit = async () => {
    if (!selectedUser) return;

    try {
      const response = await fetch(`${API}/admin/users/${selectedUser.user_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(userForm)
      });

      if (response.ok) {
        toast.success('User updated successfully');
        fetchUsers(pagination.page);
        setEditDialogOpen(false);
        setSelectedUser(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update user');
      }
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;

    try {
      const response = await fetch(`${API}/admin/users/${selectedUser.user_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('User deleted successfully');
        fetchUsers(pagination.page);
        setDeleteDialogOpen(false);
        setSelectedUser(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete user');
      }
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const openPasswordDialog = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setShowPassword(false);
    setPasswordDialogOpen(true);
  };

  const handleSetPassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    try {
      const response = await fetch(`${API}/admin/users/${selectedUser.user_id}/password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ new_password: newPassword })
      });

      if (response.ok) {
        toast.success('Password set successfully');
        setPasswordDialogOpen(false);
        setSelectedUser(null);
        setNewPassword('');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to set password');
      }
    } catch (error) {
      toast.error('Failed to set password');
    }
  };

  const handleSendResetEmail = async (user) => {
    try {
      const response = await fetch(`${API}/admin/users/${user.user_id}/send-reset-email`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send reset email');
      }
    } catch (error) {
      toast.error('Failed to send reset email');
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Manage</p>
          <h1 className="text-3xl font-bold text-white tracking-tight">Users</h1>
        </div>

        <button
          onClick={openCreateDialog}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-black font-medium hover:bg-zinc-200 transition-colors"
          data-testid="create-user-button"
        >
          <Plus className="w-4 h-4" />
          Create User
        </button>
      </div>

      {/* Search Bar */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by name or email..."
            className="w-full pl-10 pr-10 py-3 rounded-lg bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-700"
            data-testid="admin-user-search"
          />
          {searchInput && (
            <button
              type="button"
              onClick={clearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
        <button
          type="submit"
          className="px-6 py-3 rounded-lg bg-zinc-800 text-white font-medium hover:bg-zinc-700 transition-colors"
        >
          Search
        </button>
      </form>

      {searchQuery && (
        <div className="flex items-center gap-2 text-sm text-zinc-400">
          <span>Showing results for &quot;{searchQuery}&quot;</span>
          <button onClick={clearSearch} className="text-purple-400 hover:underline">Clear</button>
        </div>
      )}

      {loading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 skeleton rounded-lg" />
          ))}
        </div>
      ) : users.length > 0 ? (
        <>
          <div className="space-y-4">
            {users.map((user) => (
              <motion.div
                key={user.user_id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="glass-card rounded-xl p-4"
                data-testid={`admin-user-${user.user_id}`}
              >
                <div className="flex flex-col gap-4">
                  {/* User Info Row */}
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      {user.picture ? (
                        <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center text-lg font-bold text-white">
                          {user.name?.[0] || 'U'}
                        </div>
                      )}
                      <div>
                        <p className="font-semibold text-white">{user.name}</p>
                        <p className="text-zinc-500 text-sm">{user.email}</p>
                      </div>
                    </div>

                    <div className="flex flex-col md:flex-row md:items-center gap-3">
                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-mono ${
                          user.role === 'admin' 
                            ? 'bg-purple-500/20 text-purple-400' 
                            : 'bg-zinc-800 text-zinc-400'
                        }`}>
                          {user.role}
                        </span>
                        <span className="px-3 py-1 rounded-full text-xs font-mono bg-yellow-500/10 text-yellow-400">
                          {(user.melanin_balance || 0).toFixed(2)} MEL
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateRole(user.user_id, user.role === 'admin' ? 'user' : 'admin')}
                          className="px-3 py-1 rounded-lg bg-zinc-800 text-zinc-400 hover:bg-zinc-700 text-sm transition-colors"
                          data-testid={`toggle-role-${user.user_id}`}
                        >
                          {user.role === 'admin' ? 'Remove Admin' : 'Make Admin'}
                        </button>

                        <button
                          onClick={() => openPasswordDialog(user)}
                          className="flex items-center gap-1 px-3 py-1 rounded-lg bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 text-sm transition-colors"
                          data-testid={`set-password-${user.user_id}`}
                        >
                          <Key className="w-3 h-3" />
                          Set Password
                        </button>

                        <button
                          onClick={() => handleSendResetEmail(user)}
                          className="flex items-center gap-1 px-3 py-1 rounded-lg bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 text-sm transition-colors"
                          data-testid={`send-reset-${user.user_id}`}
                        >
                          <Mail className="w-3 h-3" />
                          Send Reset
                        </button>

                        <button
                          onClick={() => openEditDialog(user)}
                          className="flex items-center gap-1 px-3 py-1 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 text-sm transition-colors"
                          data-testid={`edit-user-${user.user_id}`}
                        >
                          <Edit className="w-3 h-3" />
                          Edit
                        </button>

                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            setDeleteDialogOpen(true);
                          }}
                          className="flex items-center gap-1 px-3 py-1 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 text-sm transition-colors"
                          data-testid={`delete-user-${user.user_id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Feature Toggles Row */}
                  <div className="flex flex-wrap items-center gap-4 pt-3 border-t border-zinc-800">
                    <span className="text-zinc-500 text-xs uppercase tracking-wider">Features:</span>
                    
                    {/* Melanin Account Toggle */}
                    <button
                      onClick={() => toggleUserFeature(user.user_id, 'melanin_account_enabled', user.melanin_account_enabled)}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs transition-colors ${
                        user.melanin_account_enabled !== false
                          ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                          : 'bg-zinc-800 text-zinc-500 hover:bg-zinc-700'
                      }`}
                      data-testid={`toggle-melanin-${user.user_id}`}
                    >
                      <span className={`w-2 h-2 rounded-full ${user.melanin_account_enabled !== false ? 'bg-green-400' : 'bg-zinc-600'}`} />
                      Melanin Account
                    </button>

                    {/* Delivery Driver Toggle */}
                    <button
                      onClick={() => toggleUserFeature(user.user_id, 'can_deliver', user.can_deliver)}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs transition-colors ${
                        user.can_deliver
                          ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
                          : 'bg-zinc-800 text-zinc-500 hover:bg-zinc-700'
                      }`}
                      data-testid={`toggle-delivery-${user.user_id}`}
                    >
                      <span className={`w-2 h-2 rounded-full ${user.can_deliver ? 'bg-blue-400' : 'bg-zinc-600'}`} />
                      Can Deliver
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {pagination.pages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => fetchUsers(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="w-10 h-10 flex items-center justify-center rounded-lg border border-zinc-800 text-zinc-400 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-4 font-mono text-sm text-zinc-400">
                {pagination.page} / {pagination.pages}
              </span>
              <button
                onClick={() => fetchUsers(pagination.page + 1)}
                disabled={pagination.page === pagination.pages}
                className="w-10 h-10 flex items-center justify-center rounded-lg border border-zinc-800 text-zinc-400 disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-20">
          <p className="text-zinc-500">No users found.</p>
        </div>
      )}

      {/* Create User Dialog */}
      <AlertDialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <AlertDialogContent className="bg-[#0F0F0F] border border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Create New User</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Add a new user to the platform
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-zinc-400 text-sm mb-1 block">Name *</label>
              <Input
                value={userForm.name}
                onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                placeholder="John Doe"
                className="bg-zinc-900 border-zinc-800 text-white"
              />
            </div>
            <div>
              <label className="text-zinc-400 text-sm mb-1 block">Email *</label>
              <Input
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                placeholder="john@example.com"
                className="bg-zinc-900 border-zinc-800 text-white"
              />
            </div>
            <div>
              <label className="text-zinc-400 text-sm mb-1 block">Role</label>
              <select
                value={userForm.role}
                onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-800 text-white"
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div>
              <label className="text-zinc-400 text-sm mb-1 block">Initial MEL Balance</label>
              <Input
                type="number"
                value={userForm.melanin_balance}
                onChange={(e) => setUserForm({ ...userForm, melanin_balance: parseFloat(e.target.value) || 0 })}
                min="0"
                step="0.1"
                className="bg-zinc-900 border-zinc-800 text-white"
              />
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-900 border-zinc-800 text-white hover:bg-zinc-800">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCreate}
              className="bg-white text-black hover:bg-zinc-200"
              data-testid="confirm-create-user-button"
            >
              Create User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit User Dialog */}
      <AlertDialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <AlertDialogContent className="bg-[#0F0F0F] border border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Edit User</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Update user information
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-zinc-400 text-sm mb-1 block">Name</label>
              <Input
                value={userForm.name}
                onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                className="bg-zinc-900 border-zinc-800 text-white"
              />
            </div>
            <div>
              <label className="text-zinc-400 text-sm mb-1 block">Email</label>
              <Input
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                className="bg-zinc-900 border-zinc-800 text-white"
              />
            </div>
            <div>
              <label className="text-zinc-400 text-sm mb-1 block">Role</label>
              <select
                value={userForm.role}
                onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-800 text-white"
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div>
              <label className="text-zinc-400 text-sm mb-1 block">MEL Balance</label>
              <Input
                type="number"
                value={userForm.melanin_balance}
                onChange={(e) => setUserForm({ ...userForm, melanin_balance: parseFloat(e.target.value) || 0 })}
                min="0"
                step="0.1"
                className="bg-zinc-900 border-zinc-800 text-white"
              />
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-900 border-zinc-800 text-white hover:bg-zinc-800">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleEdit}
              className="bg-blue-600 text-white hover:bg-blue-700"
              data-testid="confirm-edit-user-button"
            >
              Save Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete User Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-[#0F0F0F] border border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete User</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Are you sure you want to delete &quot;{selectedUser?.name}&quot;? This will also delete all their businesses, reviews, favorites, and transactions. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-900 border-zinc-800 text-white hover:bg-zinc-800">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="confirm-delete-user-button"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Set Password Dialog */}
      <AlertDialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <AlertDialogContent className="bg-[#0F0F0F] border border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Set User Password</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Set a new password for {selectedUser?.name} ({selectedUser?.email}). This will invalidate all existing sessions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="py-4">
            <label className="block text-sm font-medium text-zinc-400 mb-2">
              New Password
            </label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minimum 8 characters"
                className="bg-zinc-900 border-zinc-800 text-white pr-10"
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {newPassword && newPassword.length < 8 && (
              <p className="text-red-400 text-xs mt-1">Password must be at least 8 characters</p>
            )}
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-900 border-zinc-800 text-white hover:bg-zinc-800">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSetPassword}
              className="bg-yellow-600 text-white hover:bg-yellow-700"
              data-testid="confirm-set-password-button"
              disabled={!newPassword || newPassword.length < 8}
            >
              Set Password
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export { AdminUsers };
export default AdminUsers;
